import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Table, Button, Spinner, Alert, Pagination } from 'react-bootstrap';
import { FiCloudRain, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { fetchForecastsRequest } from '../../store/actions/forecastsActions';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminForecasts.css';

const EMPTY_FORECASTS_FILTERS = {
    search: '',
};

const AdminForecasts = () => {
    const dispatch = useDispatch();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const forecastsState = useSelector((state) => state.forecasts.list);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const {
        filters,
        setFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
    } = usePersistedSearchState('forecasts', EMPTY_FORECASTS_FILTERS, {
        persistWhen: isAuthenticated,
        withAppliedFilters: false,
    });
    const searchTerm = filters.search;

    const fetchData = () => { dispatch(fetchForecastsRequest()); };

    useEffect(() => { if (isAuthenticated) fetchData(); /* eslint-disable-next-line */ }, [isAuthenticated]);

    useEffect(() => {
        if (forecastsState.data) {
            const list = Array.isArray(forecastsState.data) ? forecastsState.data : (forecastsState.data.data || forecastsState.data.records || []);
            setRecords(list);
        } else {
            setRecords([]);
        }
        setLoading(forecastsState.loading);
        setError(forecastsState.error);
    }, [forecastsState]);

    const filteredRecords = useMemo(() => {
        if (!searchTerm) return records;
        const term = searchTerm.toLowerCase();
        return records.filter((r) => Object.values(r || {}).some((v) => v && v.toString().toLowerCase().includes(term)));
    }, [records, searchTerm]);

    const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => setCurrentPage(1), [filters.search, setCurrentPage]);

    const handlePageChange = (page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); };

    const renderPaginationItems = () => {
        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage < maxVisible - 1) startPage = Math.max(1, endPage - maxVisible + 1);
        if (startPage > 1) { items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>); if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" />); }
        for (let i = startPage; i <= endPage; i++) { items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>); }
        if (endPage < totalPages) { if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" />); items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>); }
        return items;
    };

    const getField = (obj, keys, fallback = 'N/A') => { for (const k of keys) { if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k]; } return fallback; };

    return (
        <div className="dashboard-page admin-forecasts-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCloudRain className="me-2" />
                                Gestion des Prévisions
                            </h2>
                            <p className="text-muted mb-0">Liste des prévisions météo</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-primary" onClick={fetchData} disabled={loading}>
                                <FiRefreshCw className={loading ? 'spinning' : ''} /> Actualiser
                            </Button>
                        </div>
                    </div>

                    {error && (<Alert variant="danger" className="mt-3">{error}</Alert>)}

                    <div className="table-toolbar mb-3">
                        <div className="search-box">
                            <FiSearch className="search-icon" />
                            <input type="text" className="form-control search-input" placeholder="Rechercher..." value={searchTerm} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} />
                        </div>
                    </div>

                    <div className="table-responsive table-scroll">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3">Chargement des prévisions...</p>
                            </div>
                        ) : filteredRecords.length === 0 ? (
                            <div className="text-center py-5">
                                <FiCloudRain size={48} className="text-muted mb-3" />
                                <p className="text-muted">{searchTerm ? 'Aucun enregistrement trouvé' : 'Aucun enregistrement disponible'}</p>
                            </div>
                        ) : (
                            <Table hover striped className="admin-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Zone</th>
                                        <th>Produit</th>
                                        <th>Risque</th>
                                        <th>Période</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.map((item, idx) => (
                                        <tr key={item.id || item.forecast_id || idx}>
                                            <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getField(item, ['id', 'forecast_id'], '-')}</td>
                                            <td>{getField(item, ['zone', 'city', 'country'])}</td>
                                            <td>{getField(item, ['product', 'policy_type'])}</td>
                                            <td>{getField(item, ['risk', 'claim_type'])}</td>
                                            <td>{getField(item, ['period', 'duration'])}</td>
                                            <td>{getField(item, ['date', 'created_at', 'updated_at'])}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>

                    {!loading && filteredRecords.length > itemsPerPage && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredRecords.length)} sur {filteredRecords.length}</div>
                            <Pagination>
                                <Pagination.Prev disabled={currentPage === 1} onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)} />
                                {renderPaginationItems()}
                                <Pagination.Next disabled={currentPage === totalPages} onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)} />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminForecasts;


